"use client";

import { ChevronsDownIcon, PanelLeftIcon } from "lucide-react";
import Link from "next/link";
import type { ReactNode } from "react";

import { Button } from "@/components/ui/button";
import { SearchField } from "@/components/shared/quiet-controls";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type { NavigationTab } from "./workspace-navigation-lists";
import {
  SessionScopeToggle,
  type SessionNavigationScope,
} from "./workspace-navigation-session-scope";

export type WorkspaceNavigationViewProps = {
  tab: NavigationTab;
  onTabChange: (tab: NavigationTab) => void;
  query: string;
  onQueryChange: (value: string) => void;
  sessionScope: SessionNavigationScope;
  onSessionScopeChange: (scope: SessionNavigationScope) => void;
  /** The Sessions tab's list, usually a SessionNavigationList. */
  sessionsList: ReactNode;
  /** The Channels tab's list, usually a ChannelNavigationList. */
  channelsList: ReactNode;
  onCollapse: () => void;
  /** A guest's rail: their channels, without the Sessions tab. */
  channelsOnly?: boolean;
  /** Dialogs the rail opens, mounted inside the aside. */
  children?: ReactNode;
};

/**
 * The workspace rail as markup: tabs, search, scope toggle, the two lists and
 * the "See all" footer. Every value arrives as a prop, so it renders the same
 * with live data (WorkspaceNavigation) or fixtures (the marketing product
 * bridge).
 */
export function WorkspaceNavigationView({
  tab,
  onTabChange,
  query,
  onQueryChange,
  sessionScope,
  onSessionScopeChange,
  sessionsList,
  channelsList,
  onCollapse,
  channelsOnly = false,
  children,
}: WorkspaceNavigationViewProps) {
  return (
    <aside
      aria-label="Workspace navigation"
      data-workspace-navigation
      className="flex h-full min-h-0 flex-col bg-sidebar-panel"
    >
      <Tabs
        className="min-h-0 flex-1 gap-0"
        value={tab}
        onValueChange={(value) => onTabChange(value as NavigationTab)}
      >
        <div className="flex h-10 shrink-0 items-center gap-1 border-b border-border/50 px-2">
          {channelsOnly ? (
            <h2 className="px-1.5 text-sm font-medium">Channels</h2>
          ) : (
            <TabsList aria-label="Workspace resources" variant="navigation">
              <TabsTrigger value="sessions">Sessions</TabsTrigger>
              <TabsTrigger value="channels">Channels</TabsTrigger>
            </TabsList>
          )}
          <Tooltip>
            <TooltipTrigger
              render={
                <Button
                  aria-label="Hide workspace navigation"
                  className="ml-auto translate-x-1 text-muted-foreground hover:text-foreground"
                  onClick={onCollapse}
                  size="icon-xs"
                  variant="ghost"
                />
              }
            >
              <PanelLeftIcon className="size-3.5" strokeWidth={1.75} />
            </TooltipTrigger>
            <TooltipContent side="right">Hide navigator (M)</TooltipContent>
          </Tooltip>
        </div>
        <div className="flex h-10 shrink-0 items-center border-b border-border/50 px-2">
          <SearchField
            aria-label={`Search ${tab}`}
            className="w-full"
            density="compact"
            onChange={(event) => onQueryChange(event.target.value)}
            placeholder={`Search ${tab}`}
            value={query}
          />
          {tab === "sessions" ? (
            <SessionScopeToggle
              scope={sessionScope}
              onScopeChange={onSessionScopeChange}
            />
          ) : null}
        </div>
        <TabsContent
          className="min-h-0 flex-1 overflow-y-auto py-1 scrollbar-thin"
          value="sessions"
        >
          {sessionsList}
        </TabsContent>
        <TabsContent
          className="min-h-0 flex-1 overflow-y-auto py-1 scrollbar-thin"
          value="channels"
        >
          {channelsList}
        </TabsContent>
        <Link
          className="flex h-9 shrink-0 items-center justify-between border-t border-border/50 px-3 text-xs font-medium text-muted-foreground hover:bg-muted/60 hover:text-foreground"
          href={`/workspace/${tab}`}
        >
          See all {tab}
          <ChevronsDownIcon className="size-3.5 -rotate-90" />
        </Link>
      </Tabs>
      {children}
    </aside>
  );
}
